import { EventEmitter, Injectable, OnInit } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { DetailErrorType } from '../../enums';
import { BaseComponent } from '../base/base.component';

@Injectable()
/**
 * This is the abstract detail component that can be used in the detail views.
 */
export abstract class DetailComponent<T> extends BaseComponent implements OnInit {
  public get hasError(): boolean {
    return this.error;
  }
  /**
   * The model mostly has id. This can be string or number.
   */
  public id: string | number;

  /**
   * The model, that contains the T object.
   */
  public model: T = null;

  /**
   * Error type.
   */
  public errorType: DetailErrorType;

  /**
   * Error flag.
   */
  private error = false;

  constructor() {
    super();
  }

  public ngOnInit() {
    this.startLoading();

    if (this.onModelChanged) {
      this.onModelChanged().subscribe(
        (model) => {
          this.setModel(model);
        },
        (err) => throwError(this.errorHandler(err, DetailErrorType.FailedToRefreshModel))
      );
    }

    this.getModel().subscribe(
      (model) => this.setModel(model),
      (err) => throwError(this.errorHandler(err, DetailErrorType.FailedToLoadModel))
    );
  }

  /**
   * The abstract function, that needs to be implemented in the child element. This method will get the T model from the api.
   * @returns Observable<T>
   */
  protected abstract getModel(): Observable<T>;

  /**
   * This method repopulates the model. This method uses the loading feature, and loads the model from the backend.
   */
  protected refreshModel(withLoading = true) {
    if (withLoading) {
      this.startLoading();
    }
    this.getModel().subscribe(
      (model) => this.setModel(model),
      (err) => throwError(this.errorHandler(err, DetailErrorType.FailedToLoadModel))
    );
  }

  /**
   * This method populates the model and the originalModel.
   * @param resp Succsessful response from the backend.
   */
  private setModel(response: T) {
    this.model = response;

    if (this.afterModalLoaded) {
      this.afterModalLoaded();
    }

    this.stopLoading();
  }

  /**
   * Simple error handler.
   * @param {any} error Error
   * @param {DetailErrorType} errorType Type of error
   */
  private errorHandler(error: any, errorType: DetailErrorType): Observable<T> {
    console.error(error);
    this.stopLoading();
    if (this.onError) {
      this.onError();
    }
    this.error = true;
    this.errorType = errorType ? errorType : error;
    return new Observable<T>();
  }
}

export interface DetailComponent<T> {
  // Optional methods
  onModelChanged?(): EventEmitter<T>;

  /**
   * This function runs after the model is loaded.
   *
   * @memberof DetailComponent
   */
  afterModalLoaded?(): void;
  onError(): void;
}
